import React from 'react';
import { IconType } from '@react-icons/all-files';
import { LucideIcon } from 'lucide-react';
import { RouteComponentProps } from 'react-router';
import {
  useOptionalFilterState,
  useMakeFilteredUrlFromPath,
} from './useFilter';
import { useCan } from './useRbac';
import { BreadcrumbType } from '@/components/ConnectedBreadcrumbs';
import { BilderitModuleId } from '@/generated/codegen';
import { useFeatureFlags } from './useFeatureFlags';
import type { AnalyticsModule } from '@/utils/analytics/taxonomy';

type NavItem = NavItemCollapsible | NavItemDefinite;

type NavItemBase = {
  name: string;
  label?: React.ReactNode;
  Icon?: IconType | LucideIcon;
  permission?: string;
  routePermission?: string;
  moduleId?: BilderitModuleId;
  unauthorizedTooltip?: string;
  breadcrumbs?: BreadcrumbType[];
  layout?: React.ComponentType<any>;
  alertCountKeys?: string[];
  /**
   * `false` for redirect-only entries (login redirect, slash normalisation):
   * no PostHog `$pageview` is emitted for them — see SentryTracedRoute.
   */
  trackPageview?: boolean;
};

export type NavItemDefinite = NavItemBase & {
  path: string;
  skipRoute?: boolean;
  exact?: boolean;
  strict?: boolean;
  /** A definite item can also carry secondary sub-items, rendered as a "…"
   * overflow menu next to it (see nav-main.tsx) rather than as the
   * dropdown-only case reserved for a collapsible item (no `path`). */
  items?: (NavItemDefinite | NavItemExternal)[];
  externalHref?: undefined;
  /**
   * Product area this screen belongs to (BIL-5331). Required: the analytics
   * `module` of every pageview comes from here, never from the name.
   */
  analyticsModule: AnalyticsModule;
  component: React.ComponentType<RouteComponentProps<any>>;
  // | React.ComponentType<any>;
};
/**
 * A sub-item that opens an external site in a new tab (e.g. the PostHog
 * project under "Analítica de uso"). No route, no component: useRoutes and
 * the analytics taxonomy skip it because it has no `path`.
 */
export type NavItemExternal = NavItemBase & {
  path?: undefined;
  exact?: undefined;
  component?: undefined;
  items?: undefined;
  externalHref: string;
};
type NavItemCollapsible = NavItemBase & {
  path?: undefined;
  exact?: undefined;
  component?: undefined;
  externalHref?: undefined;
  items: (NavItemDefinite | NavItemExternal)[];
};

/** Sub-items that register a route (everything but external links). */
export const isRouteNavItem = (
  item: NavItemDefinite | NavItemExternal
): item is NavItemDefinite => typeof item.path === 'string';

export type NavGroup = {
  title: string;
  navItems: NavItem[];
  Icon: LucideIcon;
  permission?: string;
  breadcrumbs?: { title: string; href?: string }[];
  alertCountKeys?: string[];
  moduleId?: BilderitModuleId;
};

export type NavDef = ReturnType<typeof useNavGroups>[number];

export const useNavGroups = (routes: NavGroup[]) => {
  const can = useCan();
  const { isFeatureEnabled } = useFeatureFlags();
  const filter = useOptionalFilterState();
  const make = useMakeFilteredUrlFromPath();
  const canWithFilter = React.useCallback(
    (action?: string) =>
      action ? can(action, { filterQuery: filter.query }) : false,
    [can, filter.query]
  );

  const isExplicitlyVisible = React.useCallback(
    (item: NavItemBase) =>
      canWithFilter(item.permission) || canWithFilter(item.routePermission),
    [canWithFilter]
  );

  const isItemVisible = React.useCallback(
    (item: NavItem | NavItemExternal, groupVisible: boolean): boolean => {
      if (item.items) {
        return item.items.some((subItem) =>
          isItemVisible(subItem, groupVisible)
        );
      }

      if (groupVisible) {
        return !item.permission || canWithFilter(item.permission);
      }

      return isExplicitlyVisible(item);
    },
    [canWithFilter, isExplicitlyVisible]
  );

  return routes
    .filter(({ moduleId }) => !moduleId || isFeatureEnabled(moduleId))
    .map(({ navItems, ...group }) => {
      const groupVisible = canWithFilter(`sidebar:${group.title}`);
      const items = navItems
        .filter(({ moduleId }) => !moduleId || isFeatureEnabled(moduleId))
        .filter((item) => isItemVisible(item, groupVisible))
        .map((item) => ({
          ...item,
          to: item.path ? make(item.path) : undefined,
          breadcrumbs: item.breadcrumbs ?? [
            { title: group.title },
            { title: item.name, href: item.path ? make(item.path) : undefined },
          ],
          items: item.items
            ?.filter(({ moduleId }) => !moduleId || isFeatureEnabled(moduleId))
            .filter((subItem) => isItemVisible(subItem, groupVisible))
            .map((subItem) => ({
              ...subItem,
              breadcrumbs: subItem.breadcrumbs ?? [
                { title: group.title },
                {
                  title: item.label ?? item.name,
                },
                {
                  title: subItem.label ?? subItem.name,
                  href: subItem.path ? make(subItem.path) : undefined,
                },
              ],
              to: subItem.path ? make(subItem.path) : undefined,
            })),
        }));

      return {
        ...group,
        breadcrumbs: group.breadcrumbs ?? [{ title: group.title }],
        items,
      };
    })
    .filter(
      ({ title, items }) =>
        canWithFilter(`sidebar:${title}`) || items.length > 0
    );
};
